import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Eye, MousePointer, TrendingUp, Percent } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AnalyticsSummary {
  impressions: number;
  clicks: number;
  conversions: number;
  click_through_rate: number;
  conversion_rate: number;
}

interface TimeSeriesData {
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
}

interface AdvertisementAnalyticsProps {
  advertisementId: string;
}

export const AdvertisementAnalytics = ({ advertisementId }: AdvertisementAnalyticsProps) => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [advertisementId, dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch summary
      const { data: summaryData, error: summaryError } = await (supabase.rpc as any)(
        'get_advertisement_analytics_summary',
        {
          ad_id: advertisementId,
          start_date: dateRange.from.toISOString(),
          end_date: dateRange.to.toISOString(),
        }
      );

      if (summaryError) throw summaryError;
      if (summaryData && Array.isArray(summaryData) && summaryData.length > 0) {
        setSummary(summaryData[0]);
      }

      // Fetch time series data
      const { data: analyticsData, error: analyticsError } = await (supabase
        .from as any)('advertisement_analytics')
        .select('event_type, created_at')
        .eq('advertisement_id', advertisementId)
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString())
        .order('created_at');

      if (analyticsError) throw analyticsError;

      // Group by date
      const grouped = (analyticsData || []).reduce((acc: Record<string, TimeSeriesData>, item: any) => {
        const date = format(new Date(item.created_at), 'dd/MM');
        if (!acc[date]) {
          acc[date] = { date, impressions: 0, clicks: 0, conversions: 0 };
        }
        if (item.event_type === 'impression') acc[date].impressions++;
        if (item.event_type === 'click') acc[date].clicks++;
        if (item.event_type === 'conversion') acc[date].conversions++;
        return acc;
      }, {});

      setTimeSeriesData(Object.values(grouped));
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des statistiques...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Statistiques de performance</h3>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[300px] justify-start text-left">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {format(dateRange.from, 'dd MMM yyyy', { locale: fr })} -{' '}
              {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex gap-2 p-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDateRange({ from: subDays(new Date(), 7), to: new Date() })
                }
              >
                7 jours
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDateRange({ from: subDays(new Date(), 30), to: new Date() })
                }
              >
                30 jours
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDateRange({ from: subDays(new Date(), 90), to: new Date() })
                }
              >
                90 jours
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Impressions</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.impressions || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clics</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.clicks || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.conversions || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de clic (CTR)</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.click_through_rate || 0}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de conversion</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.conversion_rate || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Évolution des impressions et clics</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="impressions"
                  stroke="hsl(var(--primary))"
                  name="Impressions"
                />
                <Line
                  type="monotone"
                  dataKey="clicks"
                  stroke="hsl(var(--chart-2))"
                  name="Clics"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Performance par événement</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="impressions" fill="hsl(var(--primary))" name="Impressions" />
                <Bar dataKey="clicks" fill="hsl(var(--chart-2))" name="Clics" />
                <Bar dataKey="conversions" fill="hsl(var(--chart-3))" name="Conversions" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
